import React, { useState, useEffect } from 'react';
import './Modals.css';
import { FaEye, FaEyeSlash, FaKey, FaCheck, FaTimes, FaExclamationTriangle, FaUser, FaEnvelope, FaUserTie } from 'react-icons/fa';
import { generateRandomPassword, copyToClipboard } from '../utils/passwordGenerator';
import { supabase, callAdminFunction } from '../supabaseClient';
import { sendCredentialsEmail } from './emailService';
import { BiMailSend } from "react-icons/bi";

function CreateUserModal({ onClose, onSave, userType, prefilledEmail = '' }) {
    const [email, setEmail] = useState(prefilledEmail);
    const [role, setRole] = useState(userType === 'Empleado' ? 'Empleado' : 'CEO');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [copySuccess, setCopySuccess] = useState(false);
    const [checkingEmail, setCheckingEmail] = useState(false);
    const [canSendCredentials, setCanSendCredentials] = useState(false);

    // Password validation states
    const [validations, setValidations] = useState({
        hasUppercase: false,
        hasNumber: false,
        hasMinLength: false,
        passwordsMatch: false,
        validEmail: false,
        emailExistsInEmployees: false,
        emailNotInUse: true  // New validation state
    });

    // Validate email format
    const validateEmail = (email) => {
        const re = /^[a-zA-Z0-9._%+-]+@wsp\.com\.mx$/;
        return re.test(email);
    };

    // Check if email exists in employees table
    const checkEmailExistsInEmployees = async (email) => {
        if (!email || !validateEmail(email)) return false;

        try {
            const { data, error, count } = await supabase
                .from('employees')
                .select('wsp_email', { count: 'exact' })
                .eq('wsp_email', email);

            if (error) throw error;
            return count > 0;
        } catch (error) {
            console.error('Error checking email in employees table:', error);
            return false;
        }
    };

    // Check if email is already in use by another user
    const checkEmailNotInUse = async (email) => {
        if (!email || !validateEmail(email)) return true;

        try {
            const { data, error, count } = await supabase
                .from('users')
                .select('wsp_email', { count: 'exact' })
                .eq('wsp_email', email);

            if (error) throw error;
            return count === 0; // Return true if email is NOT in use
        } catch (error) {
            console.error('Error checking email in users table:', error);
            return false;
        }
    };

    // Check if email exists when it changes
    useEffect(() => {
        const validateEmail = async () => {
            if (!email) return;

            setCheckingEmail(true);

            try {
                // For employee type users, check if email exists in employees table
                let emailExistsInEmployees = true;
                if (userType === 'Empleado') {
                    emailExistsInEmployees = await checkEmailExistsInEmployees(email);
                }

                // Check if email is already in use by another user
                const emailNotInUse = await checkEmailNotInUse(email);

                setValidations(prev => ({
                    ...prev,
                    emailExistsInEmployees,
                    emailNotInUse
                }));
            } finally {
                setCheckingEmail(false);
            }
        };

        validateEmail();
    }, [email, userType]);

    // You might want to trigger validation when component mounts with prefilled email
    useEffect(() => {
        if (prefilledEmail) {
            // Trigger validation for the prefilled email
            const validateEmail = async () => {
                if (!prefilledEmail) return;

                setCheckingEmail(true);

                try {
                    // Check validations for the prefilled email
                    let emailExistsInEmployees = true;
                    if (userType === 'Empleado') {
                        emailExistsInEmployees = await checkEmailExistsInEmployees(prefilledEmail);
                    }

                    const emailNotInUse = await checkEmailNotInUse(prefilledEmail);

                    setValidations(prev => ({
                        ...prev,
                        emailExistsInEmployees,
                        emailNotInUse
                    }));
                } finally {
                    setCheckingEmail(false);
                }
            };

            validateEmail();
        }
    }, [prefilledEmail]);

    // Check validations when inputs change
    useEffect(() => {
        const hasUppercase = /[A-Z]/.test(password);
        const hasNumber = /[0-9]/.test(password);
        const hasMinLength = password.length >= 6;
        const passwordsMatch = password === confirmPassword && confirmPassword !== '';
        const validEmail = validateEmail(email);

        setValidations(prev => ({
            ...prev,
            hasUppercase,
            hasNumber,
            hasMinLength,
            passwordsMatch,
            validEmail
        }));

        // Enable button when all validations pass and role is selected
        const allStandardValidationsPassed = hasUppercase && hasNumber && hasMinLength && passwordsMatch && validEmail;

        // Additional validations based on userType and email availability
        const emailValidationsPassed = validations.emailNotInUse &&
            (userType !== 'Empleado' || validations.emailExistsInEmployees);

        const allValidationsPassed = allStandardValidationsPassed && emailValidationsPassed;

        setIsButtonDisabled(!allValidationsPassed || !role || checkingEmail);
    }, [email, password, confirmPassword, role, validations.emailExistsInEmployees, validations.emailNotInUse, userType, checkingEmail]);

    // Add an effect to determine when to show the credentials button
    useEffect(() => {
        // Show the button only when all these conditions are met
        const validEmail = validations.validEmail;
        const validPassword = validations.hasUppercase &&
            validations.hasNumber &&
            validations.hasMinLength;
        const passwordsMatch = validations.passwordsMatch;

        setCanSendCredentials(validEmail && validPassword && passwordsMatch && email && password);
    }, [validations, email, password]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!validations.validEmail) {
            setError('El correo debe ser un correo de WSP (@wsp.com.mx)');
            return;
        }

        // Check if email already exists
        if (!validations.emailNotInUse) {
            setError('Ya existe un usuario con este correo electrónico');
            return;
        }

        // Check if email exists in employees table for employee users
        if (userType === 'Empleado' && !validations.emailExistsInEmployees) {
            setError('Este correo no está registrado como empleado en el sistema');
            return;
        }

        if (!validations.hasUppercase || !validations.hasNumber || !validations.hasMinLength) {
            setError('La contraseña debe tener al menos una letra mayúscula, un número y 6 caracteres.');
            return;
        }

        if (!validations.passwordsMatch) {
            setError('Las contraseñas no coinciden.');
            return;
        }

        setLoading(true);

        try {
            // Use callAdminFunction instead of direct supabaseAdmin
            const { data: authData, error: authError } = await callAdminFunction('createUser', {
                user: {
                    email,
                    password,
                    email_confirm: true
                }
            });

            if (authError) {
                throw new Error(authError.message);
            }

            // Then, add the user to your custom users table with their role
            const { error: dbError } = await supabase
                .from('users')
                .insert([
                    {
                        wsp_email: email,
                        role,
                        created_at: new Date().toISOString()
                    }
                ]);

            if (dbError) {
                // If there was an error adding to the users table, we should clean up
                // by deleting the auth user we just created
                await callAdminFunction('deleteUser', { userId: authData.user.id });
                throw new Error(dbError.message);
            }

            // Call the onSave function with the new user data
            const success = await onSave({
                email,
                role,
                password
            });

            // Only close the modal if the save was successful
            if (success) {
                onClose();
            }
        } catch (error) {
            setLoading(false);
            setError(`Error al crear usuario: ${error.message}`);
        }
    };

    // Generate a random password and update both fields
    const handleGeneratePassword = async () => {
        const newPassword = generateRandomPassword(12); // Generate a 12-character password
        setPassword(newPassword);
        setConfirmPassword(newPassword);

        // Copy to clipboard
        const copied = await copyToClipboard(newPassword);
        if (copied) {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000); // Hide the success indicator after 2 seconds
        }
    };

    // Add a function to handle sending credentials
    const handleSendCredentials = () => {
        sendCredentialsEmail({
            email,
            password
        });
    };

    return (
        <div className="modal-backdrop">
            <div className="modal-content">
                <h2>
                    {userType === 'Empleado' ? (
                        <>
                            <FaUser className="modal-title-icon" />
                            Crear Nuevo Usuario
                        </>
                    ) : (
                        <>
                            <FaUserTie className="modal-title-icon" />
                            Crear Nuevo Gerente
                        </>
                    )}
                </h2>

                {error && (
                    <div className="error-message">
                        <FaExclamationTriangle />
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="email">
                            <FaEnvelope className="input-icon" />
                            Correo electrónico
                        </label>
                        <input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="correo@wsp.com.mx"
                            required
                            disabled={loading}
                        />
                        {email && !validations.validEmail && (
                            <div className="validation-error">
                                <FaTimes />
                                Debe ser un correo WSP válido (@wsp.com.mx)
                            </div>
                        )}
                        {email && validations.validEmail && !validations.emailNotInUse && !checkingEmail && (
                            <div className="validation-error">
                                <FaTimes />
                                Ya existe un usuario con este correo electrónico
                            </div>
                        )}
                        {userType === 'Empleado' && email && validations.validEmail && validations.emailNotInUse && !validations.emailExistsInEmployees && !checkingEmail && (
                            <div className="validation-error">
                                <FaTimes />
                                Este correo no está registrado como empleado en el sistema
                            </div>
                        )}
                        {checkingEmail && (
                            <div className="validation-info">
                                Verificando correo...
                            </div>
                        )}
                    </div>

                    <div className="form-group">
                        <label htmlFor="role">
                            <FaUserTie className="input-icon" />
                            Rol
                        </label>
                        <select
                            id="role"
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                            required
                            disabled={loading || userType === 'Empleado'}
                        >
                            {userType === 'Empleado' ? (
                                <option value="Empleado">Empleado</option>
                            ) : (
                                <>
                                    <option value="CEO">CEO</option>
                                    <option value="Fábrica ABAP">Fábrica ABAP</option>
                                    <option value="Staffing">Staffing</option>
                                    <option value="RH">Recursos Humanos</option>
                                    <option value="PMO-IT">PMO / IT</option>
                                    <option value="Finanzas">Finanzas</option>
                                </>
                            )}
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">
                            <FaKey className="input-icon" />
                            Contraseña
                        </label>
                        <div className="password-input-container">
                            <input
                                id="password"
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                disabled={loading}
                            />
                            <button
                                type="button"
                                className="password-toggle-button"
                                onClick={() => setShowPassword(!showPassword)}
                                tabIndex="-1"
                            >
                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                            <button
                                type="button"
                                className="generate-password-button"
                                onClick={handleGeneratePassword}
                                title="Generar contraseña aleatoria"
                                tabIndex="-1"
                                disabled={loading}
                            >
                                {copySuccess ? <FaCheck className="copy-success" /> : <FaKey />}
                            </button>
                        </div>
                        {password && (
                            <div className="password-requirements">
                                <div className={validations.hasMinLength ? "requirement-met" : "requirement"}>
                                    {validations.hasMinLength ? <FaCheck /> : <FaTimes />}
                                    Al menos 6 caracteres
                                </div>
                                <div className={validations.hasUppercase ? "requirement-met" : "requirement"}>
                                    {validations.hasUppercase ? <FaCheck /> : <FaTimes />}
                                    Al menos una letra mayúscula
                                </div>
                                <div className={validations.hasNumber ? "requirement-met" : "requirement"}>
                                    {validations.hasNumber ? <FaCheck /> : <FaTimes />}
                                    Al menos un número
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="form-group">
                        <label htmlFor="confirm-password">
                            <FaKey className="input-icon" />
                            Confirmar contraseña
                        </label>
                        <div className="password-input-container">
                            <input
                                id="confirm-password"
                                type={showConfirmPassword ? "text" : "password"}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                disabled={loading}
                            />
                            <button
                                type="button"
                                className="password-toggle-button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                tabIndex="-1"
                            >
                                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                        </div>
                        {confirmPassword && !validations.passwordsMatch && (
                            <div className="validation-error">
                                <FaTimes />
                                Las contraseñas no coinciden
                            </div>
                        )}
                    </div>

                    {canSendCredentials && (
                        <button
                            type="button"
                            className="send-credentials-button"
                            onClick={handleSendCredentials}
                            title="Enviar credenciales por correo"
                        >
                            <BiMailSend className="email-icon" />
                            Enviar credenciales por correo
                        </button>
                    )}

                    <div className="modal-buttons">
                        <button
                            type="button"
                            className="cancel-button"
                            onClick={onClose}
                            disabled={loading}
                        >
                            <FaTimes />
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className={`save-button ${isButtonDisabled ? 'disabled' : ''}`}
                            disabled={isButtonDisabled || loading}
                        >
                            <FaCheck />
                            {loading ? 'Creando...' : 'Crear Usuario'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default CreateUserModal;